import React, { useMemo } from "react";
import { UserAvatar } from "components/Avatar/UserAvatar";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import { Autocomplete } from "components/Autocomplete";
import { User } from "interfaces/user";

interface UserEntry {
  id: number | null;
  name: string;
  email: string | null;
}

export interface UserDropdownProps {
  label: string;
  users: User[];
  selectedUserId: number | null;
  onChangeSelectedUserId(userId: number | null): void;
  avatarTooltip?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  unassignedValue?: string;
}

export const UserDropdown = ({
  label,
  users,
  selectedUserId,
  onChangeSelectedUserId,
  avatarTooltip,
  error = false,
  helperText = "",
  disabled = false,
  unassignedValue
}: UserDropdownProps): JSX.Element => {
  const UnassignedUser = useMemo(
    () =>
      unassignedValue ? { id: null, name: unassignedValue, email: null } : null,
    [unassignedValue]
  );

  const formattedUsers = useMemo(() => {
    const userList: UserEntry[] = users
      ? users.map(({ id, first_name, last_name, email }) => ({
          id,
          name: `${first_name} ${last_name}`,
          email
        }))
      : [];

    const sortedUserList = userList.sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    return UnassignedUser
      ? [UnassignedUser, ...sortedUserList]
      : sortedUserList;
  }, [users, UnassignedUser]);

  const selectedUser = useMemo(() => {
    return (
      formattedUsers.find(user => user.id === selectedUserId) ?? UnassignedUser
    );
  }, [selectedUserId, formattedUsers, UnassignedUser]);

  return (
    <>
      {!users ? (
        <Skeleton variant="rectangular" height={45} />
      ) : (
        <Grid container alignItems="center" wrap="nowrap" spacing={1}>
          <Grid item>
            <UserAvatar
              name={selectedUser?.name ?? ""}
              toolTip={avatarTooltip}
              useAvatar={true}
            />
          </Grid>
          <Grid item xs>
            <Autocomplete
              id={`${label}-user-list`}
              label={label}
              error={error}
              helperText={helperText}
              disabled={disabled}
              autoComplete
              autoHighlight
              autoSelect
              options={formattedUsers}
              getOptionLabel={(option: UserEntry) => {
                const emailString = option.email ? `(${option.email})` : "";
                return `${option.name} ${emailString}`;
              }}
              value={selectedUser}
              isOptionEqualToValue={(option: UserEntry, value: UserEntry) =>
                option.id === value.id
              }
              onChange={(_event: any, value: UserEntry) => {
                /** When we click an option it returns 'object {id, name}'.
                 * But 'null' with close(clear) button */
                return onChangeSelectedUserId(value?.id ?? null);
              }}
              data-qa="CP-Select-Case-Assignee"
              placeholder={undefined}
              loading={undefined}
              size={undefined}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};
